'use client'

import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

// ============================================================================
// ATMOSPHERE — soft drifting clouds for the daytime cozy feel ☁️
// ============================================================================
export function CloudScape({ tone = 'day' }: { tone?: 'day' | 'night' }) {
  const clouds = [
    { top: '8%', size: 130, delay: 0, dur: 70, op: tone === 'day' ? 0.7 : 0.25 },
    { top: '22%', size: 90, delay: 18, dur: 90, op: tone === 'day' ? 0.5 : 0.18 },
    { top: '40%', size: 160, delay: 40, dur: 110, op: tone === 'day' ? 0.45 : 0.16 },
    { top: '62%', size: 100, delay: 8, dur: 80, op: tone === 'day' ? 0.4 : 0.14 },
    { top: '78%', size: 140, delay: 30, dur: 100, op: tone === 'day' ? 0.55 : 0.2 },
  ]
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden">
      {clouds.map((c, i) => (
        <div
          key={i}
          className="absolute animate-driftSlow"
          style={{
            top: c.top,
            left: 0,
            animationDuration: `${c.dur}s`,
            animationDelay: `-${c.delay}s`,
            opacity: c.op,
          }}
        >
          <Cloud size={c.size} tone={tone} />
        </div>
      ))}
    </div>
  )
}

function Cloud({ size, tone }: { size: number; tone: 'day' | 'night' }) {
  const fill = tone === 'day' ? '#ffffff' : '#c9d0ec'
  return (
    <svg width={size} height={size * 0.6} viewBox="0 0 200 120" fill="none">
      <g fill={fill}>
        <ellipse cx="60" cy="80" rx="55" ry="35" />
        <ellipse cx="110" cy="65" rx="50" ry="45" />
        <ellipse cx="150" cy="85" rx="45" ry="30" />
        <ellipse cx="100" cy="90" rx="70" ry="28" />
      </g>
    </svg>
  )
}

// ============================================================================
// NIGHT SKY — twinkling stars & moon for Sleep 🌙
// ============================================================================
export function NightSky() {
  const stars = Array.from({ length: 60 }).map((_, i) => ({
    left: `${(i * 37) % 100}%`,
    top: `${(i * 53) % 100}%`,
    size: (i % 3) + 1,
    delay: (i % 7) * 0.6,
  }))
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {stars.map((s, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white animate-twinkle"
          style={{ left: s.left, top: s.top, width: s.size, height: s.size, animationDelay: `${s.delay}s` }}
        />
      ))}
      <div className="absolute right-16 top-16 h-24 w-24 rounded-full bg-[#f5f3e0] shadow-[0_0_60px_20px_rgba(245,243,224,0.35)]" />
    </div>
  )
}

// ============================================================================
// DEER SILHOUETTE — subtle deer-inspired detail 🦌
// ============================================================================
export function DeerMark({ className = '', color = '#d9bfa3' }: { className?: string; color?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill={color} aria-hidden>
      <path d="M18 6c1 4 3 7 6 9-1 3-2 6-2 9 0 8 4 13 10 13s10-5 10-13c0-3-1-6-2-9 3-2 5-5 6-9-3 2-6 3-8 6-2-2-4-3-6-3s-4 1-6 3c-2-3-5-4-8-6z" opacity="0.9"/>
      <path d="M32 27c5 0 9 4 9 12 0 7-4 12-9 12s-9-5-9-12c0-8 4-12 9-12z" opacity="0.55"/>
      <circle cx="28" cy="33" r="1.4"/>
      <circle cx="36" cy="33" r="1.4"/>
    </svg>
  )
}

// ============================================================================
// FEATURE SHELL — a full-screen page that fills the desktop, with a back
// button and smooth entrance. Every feature opens inside one of these.
// ============================================================================
export function FeatureShell({
  emoji,
  title,
  subtitle,
  onBack,
  tone = 'day',
  accent = '#a9866a',
  children,
}: {
  emoji: string
  title: string
  subtitle?: string
  onBack: () => void
  tone?: 'day' | 'night'
  accent?: string
  children: React.ReactNode
}) {
  const isNight = tone === 'night'
  return (
    <motion.div
      key={title}
      initial={{ opacity: 0, scale: 0.98, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98, y: -16 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-0 z-40 flex flex-col overflow-hidden ${
        isNight ? 'bg-gradient-to-b from-dusk-900 via-dusk-800 to-dusk-700 text-cream-100' : 'bg-gradient-to-b from-cream-50 via-cream-100 to-fawn-100 text-fawn-700'
      }`}
    >
      {/* texture layer (kept separate so it never overrides the opaque gradient) */}
      <div className={`pointer-events-none absolute inset-0 ${isNight ? 'night-grain' : 'paper-grain'}`} />
      {isNight ? <NightSky /> : <CloudScape tone="day" />}

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-12 md:py-7">
        <button
          onClick={onBack}
          className={`group flex items-center gap-2 rounded-full px-5 py-2.5 text-lg font-medium shadow-soft backdrop-blur transition-all hover:-translate-x-0.5 ${
            isNight ? 'bg-white/10 text-cream-100 hover:bg-white/20' : 'bg-white/80 text-fawn-600 hover:bg-white'
          }`}
        >
          <span className="text-xl transition-transform group-hover:-translate-x-0.5">←</span>
          <span>Back to Meadow</span>
        </button>
        <div className="flex items-center gap-2 text-sm opacity-70">
          <span>🦌</span>
          <span className="font-display tracking-wide">Hazel &amp; Fawn</span>
          <span>☁️</span>
        </div>
      </header>

      {/* Title */}
      <div className="relative z-10 px-6 pb-2 text-center md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <div className="mb-2 text-5xl md:text-6xl">{emoji}</div>
          <h1
            className="font-display text-4xl font-bold tracking-tight md:text-5xl"
            style={{ color: isNight ? '#f3e7d6' : accent }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className={`mx-auto mt-2 max-w-2xl text-xl ${isNight ? 'text-cream-200/80' : 'text-fawn-500'}`}>
              {subtitle}
            </p>
          )}
        </motion.div>
      </div>

      {/* Body */}
      <div className="relative z-10 flex-1 overflow-y-auto px-4 pb-10 pt-4 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28, duration: 0.5 }}
          className="mx-auto h-full w-full max-w-5xl"
        >
          {children}
        </motion.div>
      </div>
    </motion.div>
  )
}

// ============================================================================
// PROSE — render an AI reply that may contain **bold** and paragraphs
// ============================================================================
export function Prose({ text, className = '' }: { text: string; className?: string }) {
  const paras = text.split('\n\n')
  return (
    <div className={`prose-cozy ${className}`}>
      {paras.map((p, i) => (
        <p key={i} dangerouslySetInnerHTML={{ __html: inlineFormat(p) }} />
      ))}
    </div>
  )
}

function inlineFormat(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>')
}

// ============================================================================
// CHAT PANEL — reusable conversational UI for the AI companions
// ============================================================================
export interface ChatMessage {
  id: number
  role: 'user' | 'ai'
  text: string
  verse?: { ref: string; text: string }
}

export function ChatPanel({
  respond,
  greeting,
  placeholder,
  tone = 'day',
  accent = '#a9866a',
  aiAvatar = '🧸',
  aiName = 'Assistant',
  storageKey,
  quickPrompts = [],
  extraActions,
}: {
  respond: (input: string) => { text: string; verse?: { ref: string; text: string } }
  greeting: string
  placeholder: string
  tone?: 'day' | 'night'
  accent?: string
  aiAvatar?: string
  aiName?: string
  storageKey?: string
  quickPrompts?: string[]
  extraActions?: React.ReactNode
}) {
  const isNight = tone === 'night'
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [typing, setTyping] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const idRef = useRef(1)

  useEffect(() => {
    let restored: ChatMessage[] | null = null
    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey)
        if (raw) restored = JSON.parse(raw)
      } catch {}
    }
    if (restored && restored.length) {
      idRef.current = restored[restored.length - 1].id + 1
      setMessages(restored)
    } else {
      setMessages([{ id: 0, role: 'ai', text: greeting }])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
    if (storageKey && messages.length) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(messages.slice(-40)))
      } catch {}
    }
  }, [messages, typing, storageKey])

  const send = (raw?: string) => {
    const value = (raw ?? input).trim()
    if (!value || typing) return
    const userMsg: ChatMessage = { id: idRef.current++, role: 'user', text: value }
    setMessages((m) => [...m, userMsg])
    setInput('')
    setTyping(true)
    const { text, verse } = respond(value)
    const delay = Math.min(1400, 500 + text.length * 1.2)
    setTimeout(() => {
      setMessages((m) => [...m, { id: idRef.current++, role: 'ai', text, verse }])
      setTyping(false)
    }, delay)
  }

  const pushAi = (text: string, verse?: { ref: string; text: string }) => {
    setTyping(true)
    setTimeout(() => {
      setMessages((m) => [...m, { id: idRef.current++, role: 'ai', text, verse }])
      setTyping(false)
    }, 600)
  }

  return (
    <div className="flex h-[calc(100vh-280px)] min-h-[420px] flex-col">
      {/* Quick prompts / extra actions */}
      {(quickPrompts.length > 0 || extraActions) && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {quickPrompts.map((q) => (
            <button
              key={q}
              onClick={() => send(q)}
              className={`rounded-full px-4 py-1.5 text-base shadow-soft transition-all hover:-translate-y-0.5 ${
                isNight ? 'bg-white/10 text-cream-100 hover:bg-white/20' : 'bg-white/80 text-fawn-600 hover:bg-white'
              }`}
            >
              {q}
            </button>
          ))}
          {extraActions && <ExtraActionsContext.Provider value={pushAi}>{extraActions}</ExtraActionsContext.Provider>}
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className={`flex-1 space-y-4 overflow-y-auto rounded-3xl p-5 shadow-soft ${
          isNight ? 'bg-white/5 backdrop-blur' : 'bg-white/70 backdrop-blur'
        }`}
      >
        {messages.map((m) => (
          <MessageBubble key={m.id} msg={m} tone={tone} accent={accent} aiAvatar={aiAvatar} />
        ))}
        {typing && (
          <div className="flex items-center gap-3">
            <div className="text-2xl">{aiAvatar}</div>
            <div className={`flex gap-1.5 rounded-2xl px-4 py-3 ${isNight ? 'bg-white/10' : 'bg-cream-200/70'}`}>
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className={`h-2 w-2 rounded-full ${isNight ? 'bg-cream-100' : 'bg-fawn-400'}`}
                  animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
                  transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="mt-4 flex items-end gap-3">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send()
            }
          }}
          rows={1}
          placeholder={placeholder}
          className={`max-h-32 flex-1 resize-none rounded-2xl px-5 py-4 text-lg shadow-soft outline-none transition-all placeholder:opacity-50 ${
            isNight
              ? 'bg-white/10 text-cream-100 placeholder:text-cream-200 focus:bg-white/15'
              : 'bg-white/90 text-fawn-700 placeholder:text-fawn-400 focus:bg-white'
          }`}
        />
        <button
          onClick={() => send()}
          disabled={!input.trim() || typing}
          className="rounded-2xl px-6 py-4 text-lg font-semibold text-white shadow-cozy transition-all hover:-translate-y-0.5 disabled:opacity-40"
          style={{ backgroundColor: accent }}
        >
          Send
        </button>
      </div>
    </div>
  )
}

// Lets extraActions push AI messages into the conversation.
import { createContext, useContext } from 'react'
const ExtraActionsContext = createContext<(text: string, verse?: { ref: string; text: string }) => void>(() => {})
export function usePushAi() {
  return useContext(ExtraActionsContext)
}

function MessageBubble({
  msg,
  tone,
  accent,
  aiAvatar,
}: {
  msg: ChatMessage
  tone: 'day' | 'night'
  accent: string
  aiAvatar: string
}) {
  const isNight = tone === 'night'
  const isUser = msg.role === 'user'
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
    >
      <div className="mt-1 text-2xl">{isUser ? '🌸' : aiAvatar}</div>
      <div
        className={`max-w-[80%] rounded-3xl px-5 py-4 text-lg leading-relaxed shadow-soft ${
          isUser
            ? 'text-white'
            : isNight
            ? 'bg-white/10 text-cream-100'
            : 'bg-white text-fawn-700'
        }`}
        style={isUser ? { backgroundColor: accent } : {}}
      >
        <Prose text={msg.text} />
        {msg.verse && (
          <div className={`mt-4 rounded-2xl border-l-4 px-4 py-3 ${isNight ? 'border-blush-300 bg-white/10' : 'border-blush-300 bg-blush-200/40'}`}>
            <p className="italic leading-relaxed">&ldquo;{msg.verse.text}&rdquo;</p>
            <p className="mt-1 text-right font-display font-semibold" style={{ color: isNight ? '#f0cccc' : '#a9866a' }}>
              — {msg.verse.ref}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  )
}

// ============================================================================
// SOFT CARD — used across feature pages
// ============================================================================
export function SoftCard({
  children,
  className = '',
  tone = 'day',
}: {
  children: React.ReactNode
  className?: string
  tone?: 'day' | 'night'
}) {
  const isNight = tone === 'night'
  return (
    <div
      className={`rounded-3xl p-6 shadow-soft ${isNight ? 'bg-white/5 backdrop-blur' : 'bg-white/75 backdrop-blur'} ${className}`}
    >
      {children}
    </div>
  )
}

export { AnimatePresence }
